import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Home,
  Package,
  Users,
  BarChart3,
  Percent,
  ShoppingCart,
  Truck,
  DollarSign,
  Utensils,
  Headphones,
  Settings,
  MapPin,
  X,
  Calendar,
  Gift,
  LineChart,
  Target,
  Crown,
  ClipboardList,
  Activity,
  Zap,
  FileText,
  MessageSquare,
  Brain,
  Phone,
  Store,
  Palette,
  ChevronDown,
  ChevronRight,
  Star,
  Tags,
  PlusCircle,
  Navigation,
  Code,
  Megaphone
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { usePermissions } from "@/hooks/usePermissions";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

interface MenuItem {
  title: string;
  href: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}

interface MenuSection {
  title: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  items: MenuItem[];
  defaultOpen?: boolean;
}

const adminMenuSections: MenuSection[] = [
  {
    title: "Overview",
    icon: Home,
    defaultOpen: true,
    items: [
      { title: "Dashboard", href: "/admin/dashboard", icon: Home },
      { title: "Rider Dashboard", href: "/admin/rider-dashboard", icon: Navigation },
      { title: "Rider View", href: "/admin/rider-view", icon: Navigation }
    ]
  },
  {
    title: "Orders & Sales",
    icon: ShoppingCart,
    defaultOpen: true,
    items: [
      { title: "Orders", href: "/admin/orders", icon: ShoppingCart },
      { title: "Cash Tracking", href: "/admin/cash-tracking", icon: DollarSign },
      { title: "Cash Submissions", href: "/admin/cash-submissions", icon: ClipboardList }
    ]
  },
  {
    title: "Products & Inventory",
    icon: Package,
    defaultOpen: false,
    items: [
      { title: "Products", href: "/admin/products", icon: Package },
      { title: "Inventory", href: "/admin/inventory", icon: Package },
      { title: "Procurement", href: "/admin/procurement", icon: ClipboardList }
    ]
  },
  {
    title: "Packages & Promotions",
    icon: Gift,
    defaultOpen: false,
    items: [
      { title: "Packages", href: "/admin/packages", icon: Gift },
      { title: "Package Categories", href: "/admin/package-categories", icon: Tags },
      { title: "Package Analytics", href: "/admin/package-analytics", icon: Activity },
      { title: "Discounts", href: "/admin/discounts", icon: Percent },
      { title: "Promotional Banners", href: "/admin/promotional-banners", icon: Megaphone },
      { title: "Product Discounts", href: "/admin/product-discounts", icon: Target },
      { title: "Bulk & Loyalty", href: "/admin/bulk-loyalty", icon: Crown },
      { title: "Special Days", href: "/admin/special-days", icon: Calendar }
    ]
  },
  {
    title: "Customers & CRM",
    icon: Users,
    defaultOpen: false,
    items: [
      { title: "Customers", href: "/admin/customers", icon: Users },
      { title: "Customer Service", href: "/admin/customer-service", icon: Headphones },
      { title: "Customer Feedback", href: "/admin/feedback-management", icon: MessageSquare },
      { title: "Product Reviews", href: "/admin/product-reviews", icon: Star },
      { title: "CRM Automation", href: "/admin/crm-automation", icon: Zap },
      { title: "Automated Engagement", href: "/admin/automated-engagement", icon: MessageSquare }
    ]
  },
  {
    title: "Operations",
    icon: Utensils,
    defaultOpen: false,
    items: [
      { title: "Kitchen Display", href: "/admin/kitchen-display", icon: Utensils },
      { title: "Dispatch Management", href: "/admin/dispatcher", icon: Truck },
      { title: "Riders", href: "/admin/riders", icon: Truck },
      { title: "Delivery Zones", href: "/admin/delivery-zones", icon: MapPin },
      { title: "Delivery Locations", href: "/admin/delivery-locations", icon: MapPin }
    ]
  },
  {
    title: "Analytics & Reports",
    icon: BarChart3,
    defaultOpen: false,
    items: [
      { title: "Granular Analytics", href: "/admin/analytics/granular", icon: LineChart },
      { title: "Smart Analytics", href: "/admin/smart-analytics", icon: BarChart3 },
      { title: "Predictive Analytics", href: "/admin/predictive-analytics", icon: Brain },
      { title: "Admin Analytics", href: "/admin/admin-analytics", icon: Activity }
    ]
  },
  {
    title: "Marketing & Content",
    icon: Palette,
    defaultOpen: false,
    items: [
      { title: "Content Management", href: "/admin/content-management", icon: FileText },
      { title: "Blog Management", href: "/admin/blog-management", icon: FileText },
      { title: "Lead Magnets", href: "/admin/lead-magnets", icon: Target },
      { title: "Create Lead Magnet", href: "/admin/lead-magnets/create", icon: PlusCircle },
{ title: "Seasonal Themes", href: "/admin/seasonal-themes", icon: Calendar },
      { title: "WhatsApp Business", href: "/admin/whatsapp-business", icon: Phone }
    ]
  },
  {
    title: "Settings & Management",
    icon: Settings,
    defaultOpen: false,
    items: [
      { title: "Store Settings", href: "/admin/store-settings", icon: Store },
      { title: "User Management", href: "/admin/user-management", icon: Users },
      { title: "Settings", href: "/admin/settings", icon: Settings }
    ]
  },
  {
    title: "API Management",
    icon: Code,
    defaultOpen: false,
    items: [
      { title: "API Portal", href: "/admin/api-management", icon: Code }
    ]
  }
];

interface AdminSidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

export function AdminSidebar({ isOpen, onToggle }: AdminSidebarProps) {
  const location = useLocation();
  const currentPath = location.pathname;
  const { canAccess, isFullAdmin } = usePermissions();
  const { user, userRoles } = useAuth();
  const [riderId, setRiderId] = useState<string | null>(null);

  // Load current rider id for building dynamic Rider View link
  const isRider = (userRoles || []).some(r => r.role === "rider");
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        if (!isRider || !user?.id) return;
        const { data, error } = await supabase
          .from("riders")
          .select("id")
          .eq("user_id", user.id)
          .maybeSingle();
        if (error) {
          console.warn("Failed to load rider id for sidebar link", error);
          return;
        }
        if (mounted) setRiderId(data?.id ?? null);
      } catch (e) {
        console.warn("Error loading rider id", e);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [isRider, user?.id]);

  const [openSections, setOpenSections] = useState<Set<string>>(
    new Set(adminMenuSections.filter(section => section.defaultOpen).map(section => section.title))
  );

  // Filter menu sections and items based on user permissions
  const filteredMenuSections = adminMenuSections
    .map(section => ({
      ...section,
      items: section.items.filter(item => isFullAdmin || canAccess(item.href))
    }))
    .filter(section => section.items.length > 0); // Only show sections with accessible items

  const isActive = (href: string) => currentPath === href;
  
  const isSectionActive = (section: MenuSection) => {
    return section.items.some(item => isActive(item.href));
  };

  const toggleSection = (sectionTitle: string) => {
    const newOpenSections = new Set(openSections);
    if (newOpenSections.has(sectionTitle)) {
      newOpenSections.delete(sectionTitle);
    } else {
      newOpenSections.add(sectionTitle);
    }
    setOpenSections(newOpenSections);
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onToggle}
        />
      )}
      
      {/* Sidebar */}
      <div
        className={cn(
          "fixed top-0 left-0 h-screen bg-background border-r shadow-lg z-50 transition-transform duration-300 ease-in-out flex flex-col",
          "w-64",
          isOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        {/* Header */}
        <div className="p-2 border-b flex-shrink-0">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-foreground">Admin Panel</h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={onToggle}
              className="lg:hidden"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 min-h-0">
          <div className="px-3 space-y-2">
            {filteredMenuSections.map((section) => {
              const isOpen = openSections.has(section.title);
              const hasActiveItem = isSectionActive(section);
              
              return (
                <div key={section.title} className="space-y-1">
                  {/* Section Header */}
                  <button
                    onClick={() => toggleSection(section.title)}
                    className={cn(
                      "w-full flex items-center justify-between px-3 py-2 text-sm font-medium rounded-lg transition-colors",
                      hasActiveItem
                        ? "bg-primary/5 text-primary"
                        : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <section.icon className="h-4 w-4 flex-shrink-0" />
                      <span>{section.title}</span>
                    </div>
                    {isOpen ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </button>

                  {/* Section Items */}
                  {isOpen && (
                    <div className="ml-4 space-y-1">
                      {section.items.map((item) => {
                        const isRiderView = item.href === "/admin/rider-view";
                        const targetHref = isRiderView && riderId ? `/admin/rider-view/${riderId}` : item.href;
                         return (
                           <Link
                           key={targetHref}
                           to={targetHref}
                           onClick={() => window.innerWidth < 1024 && onToggle()}
                           className={cn(
                            "flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-colors",
                            isActive(targetHref)
                               ? "bg-primary/10 text-primary font-medium border-r-2 border-primary"
                               : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                           )}
                         >
                           <item.icon className="h-4 w-4 flex-shrink-0" />
                           <span>{item.title}</span>
                         </Link>
                         );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </nav>
      </div>
    </>
  );
}